import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  TextInput,
  Button,
  Alert,
  Anchor,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconMail } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';

export default function ForgotPasswordPage() {
  const authService = useAuthService();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await authService.forgotPassword({
        email: email.trim(),
        appUrl: window.location.origin,
      });
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Could not send reset link');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size={420} my={60}>
      <Title order={2} ta="center" mb="md">
        Reset your password
      </Title>
      <Text c="dimmed" size="sm" ta="center" mb="xl">
        Enter your email and we'll send you a link to set a new password.
      </Text>

      <Paper withBorder shadow="sm" p="lg" radius="md">
        {sent ? (
          <Stack>
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              If an account with that email exists, a reset link is on its way. Check your inbox.
            </Alert>
            <Anchor component={Link} to="/auth/login" ta="center">
              Back to sign in
            </Anchor>
          </Stack>
        ) : (
          <form onSubmit={onSubmit}>
            <Stack gap="md">
              {error && (
                <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                  {error}
                </Alert>
              )}
              <TextInput
                label="Email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                autoComplete="email"
              />
              <Button type="submit" loading={submitting} leftSection={<IconMail size={16} />} fullWidth>
                Send reset link
              </Button>
              <Anchor component={Link} to="/auth/login" ta="center" size="sm">
                Back to sign in
              </Anchor>
            </Stack>
          </form>
        )}
      </Paper>
    </Container>
  );
}
